import { Component } from '@theme/component';

/**
 * Adds previous/next arrow navigation to the horizontally scrolling timeline spots.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} scroller - The scrolling spots container.
 * @property {HTMLButtonElement} [previous] - The previous arrow.
 * @property {HTMLButtonElement} [next] - The next arrow.
 * @property {HTMLElement} [slideshowControls] - The arrows wrapper.
 *
 * @extends Component<Refs>
 */
class TimelineSpotsComponent extends Component {
  requiredRefs = ['scroller'];

  #resizeObserver = new ResizeObserver(() => this.#update());

  connectedCallback() {
    super.connectedCallback();

    this.refs.scroller.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver.observe(this.refs.scroller);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.scroller?.removeEventListener('scroll', this.#update);
    this.#resizeObserver.disconnect();
  }

  previous() {
    this.#scrollBySpot(-1);
  }

  next() {
    this.#scrollBySpot(1);
  }

  /**
   * @param {number} direction
   */
  #scrollBySpot(direction) {
    const { scroller } = this.refs;
    const spot = scroller.querySelector('.timeline-spot__bar');
    const step = spot instanceof HTMLElement ? spot.getBoundingClientRect().width : scroller.clientWidth;

    scroller.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  #update = () => {
    const { scroller, previous, next, slideshowControls } = this.refs;
    if (!scroller) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;

    if (slideshowControls) slideshowControls.hidden = maxScroll <= 1;
    if (previous) previous.disabled = scroller.scrollLeft <= 1;
    if (next) next.disabled = scroller.scrollLeft >= maxScroll - 1;
  };
}

if (!customElements.get('timeline-spots-component')) {
  customElements.define('timeline-spots-component', TimelineSpotsComponent);
}
